import { Box, TextField } from "@mui/material";
import { Contact, PaymentInformation } from "@repo/server-api";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import * as Yup from "yup";

import Loader from "@repo/legacy/components/shared/Loader";

import NumberTextField from "../fields/NumberTextField";

const PaymentCompanyInformation = forwardRef(
	(
		props: Readonly<{
			contact?: Contact | null;
			setContact?: (value: Contact) => void;
			errors?: Record<string, string | boolean | object>;
			setErrors?: (value: Record<string, boolean | string | object>) => void;
			isLoaded?: boolean;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const [info, setInfo] = useState<PaymentInformation>({
			id: "",
			__typename: "PaymentInformation",
			paymentDeadlineInDays: null,
			routeId: "",
			taxNumber: "",
			vatId: "",
		});

		const [isLoaded, setIsLoaded] = useState<boolean>(false);

		const validationSchema = Yup.object().shape({
			// departmentName: Yup.string().required(t("validation.department.required")),
		});

		useEffect(() => {
			if (props.contact && !isLoaded) {
				const paymentInfo = props.contact.additionalInformation?.find(
					(info): info is PaymentInformation =>
						(info as PaymentInformation).__typename === "PaymentInformation",
				) ?? {
					id: "",
					__typename: "PaymentInformation",
					paymentDeadlineInDays: null,
					routeId: "",
					taxNumber: "",
					vatId: "",
				};
				setInfo(paymentInfo);
				setIsLoaded(true);
			}
		}, [props.contact, props.isLoaded, isLoaded]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(info, { abortEarly: false });
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}
				return localErrors;
			}
		}, [info, props, validationSchema]);

		return (
			<div>
				{isLoaded ? (
					<div>
						<Box sx={{ width: "100%" }}>
							<TextField
								onChange={(event) => {
									const v = event.target.value ?? "";
									const newInfo = { ...info, routeId: v };
									setInfo(newInfo);
									if (props.contact && props.setContact) {
										props.setContact({
											...props.contact,
											additionalInformation: [
												...(props.contact.additionalInformation?.filter(
													(ai) => (ai as PaymentInformation).__typename !== "PaymentInformation",
												) ?? []),
												newInfo,
											],
										});
									}
								}}
								sx={{ width: "100%" }}
								value={info.routeId}
								label={t("payment-information.route-id")}
							/>
						</Box>
						<Box sx={{ width: "100%", mt: 2.5 }}>
							<TextField
								onChange={(event) => {
									const v = event.target.value ?? "";
									const newInfo = { ...info, taxNumber: v };
									setInfo(newInfo);
									if (props.contact && props.setContact) {
										props.setContact({
											...props.contact,
											additionalInformation: [
												...(props.contact.additionalInformation?.filter(
													(ai) => (ai as PaymentInformation).__typename !== "PaymentInformation",
												) ?? []),
												newInfo,
											],
										});
									}
								}}
								sx={{ width: "100%" }}
								value={info.taxNumber}
								label={t("payment-information.tax-number")}
							/>
						</Box>
						<Box sx={{ width: "100%", mt: 2.5 }}>
							<TextField
								onChange={(event) => {
									const v = event.target.value ?? "";
									const newInfo = { ...info, vatId: v };
									setInfo(newInfo);
									if (props.contact && props.setContact) {
										props.setContact({
											...props.contact,
											additionalInformation: [
												...(props.contact.additionalInformation?.filter(
													(ai) => (ai as PaymentInformation).__typename !== "PaymentInformation",
												) ?? []),
												newInfo,
											],
										});
									}
								}}
								sx={{ width: "100%" }}
								value={info.vatId}
								label={t("payment-information.vat-id")}
							/>
						</Box>
						<Box sx={{ width: "100%", mt: 1 }}>
							<NumberTextField
								integer={true}
								isLoaded={"paymentDeadlineInDays" in info}
								setValue={(v) => {
									const newInfo = { ...info, paymentDeadlineInDays: v };
									setInfo(newInfo);
									if (props.contact && props.setContact) {
										props.setContact({
											...props.contact,
											additionalInformation: [
												...(props.contact.additionalInformation?.filter(
													(ai) => (ai as PaymentInformation).__typename !== "PaymentInformation",
												) ?? []),
												newInfo,
											],
										});
									}
								}}
								value={info.paymentDeadlineInDays}
								label={t("payment-information.payment-deadline-in-days")}
							/>
						</Box>
					</div>
				) : (
					<Loader />
				)}
			</div>
		);
	},
);

PaymentCompanyInformation.displayName = "PaymentCompanyInformation";
export default PaymentCompanyInformation;
